/**
 * Composition (storefront side): an outfit / kit — a product made of other
 * products the store also sells — shows one block per part above the buy
 * button: its picture, name, price, a link to its own page and the selectors
 * of the options the buyer picks there; then the running total.
 *
 * The server sends (aldoo-na/store/product/extensions/composition publicFields):
 *
 *   product.components  [{ key, pid, label, qty, required, choose, name, thumbnail,
 *                          contentType, available, inStock, amount, currency,
 *                          discountPercent?, options, variants }]
 *   product.compositionPricing  { mode: "sum" | "fixed" | "sum-percent", percent }
 *
 * — a part's `options` are the options extension's public ones, narrowed to
 * what the outfit lets the buyer pick (`choose`) and the values it allows.
 * Every part's money (`amount`, its variants' `price`, its values'
 * `priceDelta`) comes in the outfit's currency (the server converts a part
 * priced in another), so the parts add up as they are, in the currency of
 * the outfit's own line (`line.currency`) — the total shows in it.
 *
 * The picks live in the page's selection as `components: [{ key, pid,
 * choices }]` (the line contract): a required part is always bought, an
 * optional one when the buyer ticks it. `price` mirrors the server's
 * resolveLine: each part priced as bought alone (its variant's price or its
 * own, its deltas, its discount), × qty, summed — or less `percent`, or the
 * outfit's own price for "fixed". The page takes the outfit's own discount
 * off the line as it does for any product; so does the checkout.
 *
 * Props the page passes through `<ExtensionSlot name="selectors" …>`:
 * `productUrl(part)` → a part's page (the variant-group siblings' convention;
 * falls back to `/product/<pid>`), and `renderPrice({ amount, currency,
 * total })` → how a price shows (the project's currency and discount; a
 * plain formatted amount without it).
 */
import React from "react"
import Checkbox from "aldoo-ra/Checkbox"
import { imageProps } from "aldoo-ra/CMS/url"
import { OptionSelectors, isComplete as optionsComplete, missing as optionsMissing, price as optionsPrice } from "../options/storefront"
import { useStoreExtensionText } from "../texts"

const isRecord = (value) => !!value && typeof value === "object" && !Array.isArray(value)

/** The outfit's parts, as the server listed them. */
export const partsOf = (product) => (Array.isArray(product?.components) ? product.components.filter((part) => part?.key && part.pid) : [])
const picksOf = (selection) => (Array.isArray(selection?.components) ? selection.components.filter((pick) => pick?.key) : [])
const pickOf = (selection, key) => picksOf(selection).find((pick) => pick.key === key) || null
const qtyOf = (part) => (Number.isInteger(part.qty) && part.qty > 0 ? part.qty : 1)
const isIncluded = (part, selection) => part.required !== false || !!pickOf(selection, part.key)

/** A part as the options extension reads a product: its pickable options and its variants. */
const asProduct = (part) => ({ pid: part.pid, options: part.options || [], variants: part.variants || [] })

/** The part's line as bought alone: its options' mirror, its own discount off. */
const partLine = (part, choices) => {
  const start = { pid: part.pid, sku: "", amount: Number.isFinite(part.amount) ? part.amount : null, currency: part.currency, choices: {}, choiceLabels: {} }
  const line = optionsPrice(asProduct(part), { choices }, start)
  const discount = Number(part.discountPercent) > 0 ? Number(part.discountPercent) : 0
  if (Number.isFinite(line.amount) && discount) line.amount = Math.round(line.amount * (1 - discount / 100))
  return line
}

/** Is every part the buyer takes sold, and its picks complete? */
export const isComplete = (product, selection) => {
  const parts = partsOf(product)
  if (!parts.length) return true
  const taken = parts.filter((part) => isIncluded(part, selection))
  if (!taken.length) return false
  return taken.every((part) => part.available !== false && optionsComplete(asProduct(part), { choices: pickOf(selection, part.key)?.choices || {} }))
}

/** The picks still to make in the parts the buyer takes: "Тениска: Размер". */
export const missing = (product, selection) =>
  partsOf(product)
    .filter((part) => isIncluded(part, selection) && part.available !== false)
    .flatMap((part) =>
      optionsMissing(asProduct(part), { choices: pickOf(selection, part.key)?.choices || {} }).map((entry) => ({
        key: `${part.key}:${entry.key}`,
        label: `${part.label || part.name || part.pid}: ${entry.label}`,
      }))
    )

/** The client mirror of the server's resolveLine (display only). */
export const price = (product, selection, line) => {
  const parts = partsOf(product)
  if (!parts.length) return line
  const components = []
  let sum = 0
  let priced = true
  for (const part of parts) {
    if (!isIncluded(part, selection)) continue
    const choices = isRecord(pickOf(selection, part.key)?.choices) ? pickOf(selection, part.key).choices : {}
    const own = partLine(part, choices)
    if (Number.isFinite(own.amount)) sum += own.amount * qtyOf(part)
    else priced = false
    components.push({ key: part.key, pid: part.pid, sku: own.sku || "", qty: qtyOf(part), choices: own.choices, choiceLabels: own.choiceLabels, label: part.label || part.name || part.pid })
  }
  const pricing = isRecord(product.compositionPricing) ? product.compositionPricing : {}
  const percent = Math.min(100, Math.max(0, Number(pricing.percent) || 0))
  let amount = line.amount
  if (pricing.mode !== "fixed") amount = !priced ? null : pricing.mode === "sum-percent" ? Math.round(sum * (1 - percent / 100)) : sum
  return { ...line, amount, components }
}

const formatted = ({ amount, currency }) => {
  if (!Number.isFinite(amount)) return null
  if (!currency) return (amount / 100).toFixed(2)
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: String(currency).toUpperCase() }).format(amount / 100)
  } catch {
    return `${(amount / 100).toFixed(2)} ${String(currency).toUpperCase()}`
  }
}

const hrefOf = (part, productUrl) => {
  if (typeof productUrl === "function") {
    const href = productUrl(part)
    if (href) return href
  }
  return part.url || `/product/${encodeURIComponent(part.pid)}`
}

/** One part: picture, name, price, link, include switch (optional), its selectors. */
const sameText = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase()
const labelSaysMore = (part) => !!part.label && !sameText(part.label, part.name || part.pid)

function PartBlock({ part, selection, setSelection, product, productUrl, showPrice, text, attempted }) {
  const pick = pickOf(selection, part.key)
  const included = isIncluded(part, selection)
  const choices = pick?.choices || {}
  const own = partLine(part, choices)
  const unavailable = part.available === false

  /** Write this part's pick, keeping the parts in the outfit's order. */
  const write = (next) =>
    setSelection((current) => {
      const others = picksOf(current).filter((entry) => entry.key !== part.key)
      const entries = next ? [...others, next] : others
      const order = partsOf(product).map((entry) => entry.key)
      return { ...(current || {}), components: entries.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key)) }
    })

  // the options extension's selectors, on this part's own choices
  const partSelection = { choices }
  const setPartSelection = (patch) => {
    const next = typeof patch === "function" ? patch(partSelection) : { ...partSelection, ...(patch || {}) }
    write({ key: part.key, pid: part.pid, choices: isRecord(next?.choices) ? next.choices : {} })
  }
  const toggle = (on) => write(on ? { key: part.key, pid: part.pid, choices } : null)

  return (
    <div className={`flex flex-col gap-3 rounded-lg border border-black/10 p-3 ${unavailable ? "opacity-60" : ""}`} data-testid={`composition-part-${part.key}`}>
      <div className="flex items-start gap-3">
        <a href={hrefOf(part, productUrl)} className="block shrink-0 w-16 h-16 rounded-md overflow-hidden bg-black/5" aria-label={part.name || part.label}>
          {part.thumbnail && <img {...imageProps(part.thumbnail, "64px")} alt="" loading="lazy" className="w-full h-full object-cover" />}
        </a>
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          {/* the part's own label only when it says more than the product's name (an import names it the same) */}
          {(labelSaysMore(part) || qtyOf(part) > 1) && (
            <span className="text-xs uppercase tracking-wide opacity-70">
              {labelSaysMore(part) ? part.label : ""}
              {qtyOf(part) > 1 && `${labelSaysMore(part) ? " " : ""}× ${qtyOf(part)}`}
            </span>
          )}
          <span className="text-sm font-semibold leading-tight line-clamp-2">{part.name || part.pid}</span>
          {Number.isFinite(own.amount) && <span className="text-sm">{showPrice({ amount: own.amount * qtyOf(part), currency: part.currency, total: false })}</span>}
          <a href={hrefOf(part, productUrl)} className="text-xs underline opacity-80 hover:opacity-100" data-testid="composition-part-link">
            {text("view_product")}
          </a>
        </div>
        {part.required === false && !unavailable && (
          <Checkbox isOn={!!pick} onChange={(on) => toggle(!!on)} label={text("add_part")} labelClassName="text-sm" />
        )}
      </div>
      {included && !unavailable && part.options?.length > 0 && <OptionSelectors product={asProduct(part)} selection={partSelection} setSelection={setPartSelection} attempted={attempted} />}
    </div>
  )
}

/** The parts (the total is the product price above). */
export function CompositionSelectors({ product, selection, setSelection, productUrl, renderPrice, attempted = false, className = "" }) {
  const parts = partsOf(product)
  const text = useStoreExtensionText()
  if (!parts.length) return null
  const showPrice = typeof renderPrice === "function" ? renderPrice : formatted

  return (
    <div className={`flex flex-col gap-3 ${className}`} data-testid="composition-selectors">
      {parts.map((part) => (
        <PartBlock key={part.key} part={part} product={product} selection={selection} setSelection={setSelection} productUrl={productUrl} showPrice={showPrice} text={text} attempted={attempted} />
      ))}
    </div>
  )
}

export const StorefrontExtension = {
  id: "composition",
  label: "Composition",
  slots: { selectors: CompositionSelectors },
  isComplete,
  missing,
  price,
}

export default StorefrontExtension
